import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

import {
  AuthenticatedGuard,
  authenticatedGuard
} from '@bling-fe/shared/core/shared/guards/authenticated.guard';
import { RedirectIfLoggedIn } from '@bling-fe/shared/core/shared/guards/redirect-if-logged-in.guard';
import { RedirectIfTermsAccepted } from '@bling-fe/shared/core/shared/guards/redirect-if-terms-accepted.guard';
import {
  TermsAcceptedGuard,
  termsAcceptedGuard
} from '@bling-fe/shared/core/shared/guards/terms-accepted.guard';
import { ReservationsEnabledGuard } from '@bling-fe/shared/core/shared/guards/reservations-enabled.guard';
import {
  TenantSelectedGuard,
  tenantSelectedGuard
} from '@bling-fe/shared/core/shared/guards/tenant-selected.guard';
import { CanSelectTenantGuard } from '@bling-fe/shared/core/shared/guards/can-select-tenant.guard';
import { instantMessagingEnabledGuard } from '@bling-fe/shared/core/shared/guards/instant-messaging-enabled.guard';
import { maintenanceModeGuard } from '@bling-fe/shared/core/shared/guards/maintenance-mode.guard';
import {
  externalChangelogConfiguredGuard,
  externalHelpPageConfiguredGuard,
  externalPrivacyPolicyConfiguredGuard,
  externalTermsAndConditionConfiguredGuard,
  externalTermsOfUseConfiguredGuard
} from '@bling-fe/shared/core/shared/guards/static-links.guard';

import { AppLayoutComponent } from '@bling-fe/shared/core/core/layout/app-layout/app-layout.component';
import { BasicLayoutComponent } from '@bling-fe/shared/core/core/layout/basic-layout/basic-layout.component';
import { ResetPasswordGuard } from '@bling-fe/shared/core/shared/guards/reset-password.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full'
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('@bling-fe/shared/feature-maintenance-mode').then(
        m => m.SharedMaintenanceModeComponent
      )
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [maintenanceModeGuard],
    children: [
      {
        path: 'home',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/home/home.module').then(
            m => m.HomePageModule
          )
      },
      {
        path: 'community',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/community/community.module'
          ).then(m => m.CommunityPageModule)
      },
      {
        path: 'my-profile',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/my-profile/my-profile.module'
          ).then(m => m.MyProfilePageModule)
      },
      {
        path: 'my-profile/delete-account',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/my-profile-delete-account/my-profile-delete-account.module'
          ).then(m => m.MyProfileDeleteAccountPageModule)
      },
      {
        path: 'calendar',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/calendar/calendar.module'
          ).then(m => m.CalendarPageModule)
      },
      {
        path: 'my-tenant',
        canActivateChild: [
          AuthenticatedGuard,
          TenantSelectedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/my-tenant/my-tenant.module'
          ).then(m => m.MyTenantPageModule)
      },
      {
        path: 'new',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/add-new/add-new.module').then(
            m => m.AddNewPageModule
          )
      },
      {
        path: 'edit',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/edit/edit.module').then(
            m => m.EditModule
          )
      },
      {
        path: 'under-construction',
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/under-construction/under-construction.module'
          ).then(m => m.UnderConstructionPageModule)
      },
      {
        path: 'post/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/post-detail/post-detail.module'
          ).then(m => m.PostDetailPageModule)
      },
      {
        path: 'event/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/event-detail/event-detail.module'
          ).then(m => m.EventDetailPageModule)
      },
      {
        path: 'documents/:id',
        canActivate: [
          tenantSelectedGuard,
          authenticatedGuard,
          termsAcceptedGuard
        ],
        loadComponent: () =>
          import(
            '@bling-fe/shared/feature-pages/document-open/document-open.page'
          ).then(m => m.DocumentOpenPage)
      },
      {
        path: 'ticket/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        data: { ticketListUrlPrefix: '/my-tenant/tickets/' },
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/repair-message-detail/repair-message-detail.module'
          ).then(m => m.RepairMessageDetailPageModule)
      },
      {
        path: 'hourly-reservable/:type/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          ReservationsEnabledGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/hourly-reservable-detail/hourly-reservable-detail.module'
          ).then(m => m.HourlyReservableDetailPageModule)
      },
      {
        path: 'hourly-reservation/:type/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          ReservationsEnabledGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/hourly-reservation-detail/hourly-reservation-detail.module'
          ).then(m => m.HourlyReservationDetailPageModule)
      },
      {
        path: 'daily-reservable/:type/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          ReservationsEnabledGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/daily-reservable-detail/daily-reservable-detail.module'
          ).then(m => m.DailyReservableDetailPageModule)
      },
      {
        path: 'daily-reservation/:type/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          ReservationsEnabledGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/daily-reservation-detail/daily-reservation-detail.module'
          ).then(m => m.DailyReservationDetailPageModule)
      },
      {
        path: 'profile/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/profile/profile.module').then(
            m => m.ProfilePageModule
          )
      },
      {
        path: 'instant-messaging/:id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard,
          instantMessagingEnabledGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/instant-messaging/instant-messaging.module'
          ).then(m => m.InstantMessagingPageModule)
      },
      {
        path: 'group/:group_id',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/group-detail/group-detail.module'
          ).then(m => m.GroupDetailPageModule)
      },
      {
        path: 'changelog',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard,
          externalChangelogConfiguredGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/changelog/changelog.module'
          ).then(m => m.ChangelogPageModule)
      },
      {
        path: 'help',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard,
          externalHelpPageConfiguredGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/help/help.module').then(
            m => m.HelpPageModule
          )
      },
      {
        path: 'faq',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard
        ],
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/faq/faq.module').then(
            m => m.FaqPageModule
          )
      },
      {
        path: 'privacy-policy',
        canActivateChild: [
          TenantSelectedGuard,
          AuthenticatedGuard,
          TermsAcceptedGuard,
          externalPrivacyPolicyConfiguredGuard
        ],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/privacy-policy/privacy-policy.module'
          ).then(m => m.PrivacyPolicyPageModule)
      },
      {
        path: 'terms-of-use',
        canActivateChild: [externalTermsOfUseConfiguredGuard],
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/terms-of-use/terms-of-use.module'
          ).then(m => m.TermsOfUsePageModule)
      },
      {
        path: 'legal-notice',
        loadChildren: () =>
          import(
            '@bling-fe/shared/feature-pages/legal-notice/legal-notice.module'
          ).then(m => m.LegalNoticePageModule)
      },
      {
        path: 'contact',
        loadChildren: () =>
          import('@bling-fe/shared/feature-pages/contact/contact.module').then(
            m => m.ContactPageModule
          )
      },
      {
        path: 'oauth-verify-is-authenticated',
        canActivate: [
          authenticatedGuard,
          tenantSelectedGuard,
          termsAcceptedGuard
        ],
        loadComponent: () =>
          import(
            '@bling-fe/shared/feature-pages/oauth-verify-is-authenticated/oauth-verify-is-authenticated.page'
          ).then(m => m.OauthVerifyIsAuthenticatedPage)
      },
      {
        path: 'not-found',
        loadComponent: () =>
          import('@bling-fe/shared/feature-not-found').then(
            m => m.SharedFeatureNotFoundComponent
          )
      }
    ]
  },
  {
    path: 'login',
    component: BasicLayoutComponent,
    canActivateChild: [TenantSelectedGuard, RedirectIfLoggedIn],
    loadChildren: () =>
      import('@bling-fe/shared/feature-pages/login/login.module').then(
        m => m.LoginPageModule
      )
  },
  {
    path: 'reset-password',
    component: BasicLayoutComponent,
    canActivateChild: [TenantSelectedGuard, ResetPasswordGuard],
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/reset-password/reset-password.module'
      ).then(m => m.ResetPasswordPageModule)
  },
  {
    path: 'email-confirmation',
    component: BasicLayoutComponent,
    canActivateChild: [TenantSelectedGuard],
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/email-confirmation/email-confirmation.module'
      ).then(m => m.EmailConfirmationPageModule)
  },
  {
    path: 'onboarding',
    component: BasicLayoutComponent,
    canActivateChild: [CanSelectTenantGuard],
    data: { pageBG: 'var(--ion-color-light)' },
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/onboarding/onboarding.module'
      ).then(m => m.OnboardingPageModule)
  },
  {
    path: 'terms-and-conditions',
    component: BasicLayoutComponent,
    canActivateChild: [
      TenantSelectedGuard,
      AuthenticatedGuard,
      RedirectIfTermsAccepted
    ],
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/accept-terms/accept-terms.module'
      ).then(m => m.AcceptTermsPageModule)
  },
  {
    path: 'terms-and-conditions-public',
    canActivateChild: [externalTermsAndConditionConfiguredGuard],
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/terms-and-conditions/terms-and-conditions.module'
      ).then(m => m.TermsAndConditionsPageModule)
  },
  {
    path: 'privacy-policy-public',
    component: BasicLayoutComponent,
    canActivateChild: [externalPrivacyPolicyConfiguredGuard],
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/privacy-policy/privacy-policy.module'
      ).then(m => m.PrivacyPolicyPageModule)
  },
  {
    path: 'payment',
    component: BasicLayoutComponent,
    loadChildren: () =>
      import(
        '@bling-fe/shared/feature-pages/payment-status/payment-status.module'
      ).then(m => m.PaymentSuccessfulPageModule)
  },
  {
    path: 'event-participations',
    loadComponent: () =>
      import(
        '@bling-fe/shared/feature-pages/external-event-participation/external-event-participation.page'
      ).then(m => m.ExternalEventParticipationPage)
  },
  // default path is home
  {
    path: '**',
    component: AppLayoutComponent,
    canActivate: [maintenanceModeGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@bling-fe/shared/feature-not-found').then(
            m => m.SharedFeatureNotFoundComponent
          )
      }
    ]
  }
];
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
